import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

import { fmtMoney } from "../bexio/bexioFormat";
import { compactAxisTick } from "../../../pages/admin/revenueAnalytics/revenueAnalyticsFormat";
import { TREND_SERIES, selectRevenueTrend } from "./overviewChartSelectors";

/**
 * "Umsatzentwicklung" — the months the selection covers.
 *
 * THE SERIES IS THE SELECTION (P3b). A three-month period draws three points, a
 * twelve-month one twelve, and a cross-year window is ordered by the backend rather
 * than re-sorted here. A SINGLE month keeps its six-month run-up, because one point
 * is not a trend — that is the one explicit `trend_months` the hook sends.
 *
 * NO SECOND REQUEST, AND EMPHATICALLY NO REQUEST PER MONTH. Every point comes from
 * `revenue-analytics.trend`, which the request behind the KPI cards already returns.
 *
 * NO COST LINE. The reference design pairs revenue with costs, and there is no
 * historical cost series to pair it with: the A2 contract answers for ONE month at a
 * time. Charting six months of revenue against a single repeated cost figure, or
 * firing six cost requests to fake a series, would both put a number on screen that
 * nobody measured. The cost card beside this one shows what we actually have.
 *
 * THE AXES ARE NAMED, NOT ASSUMED. "Fakturiert nach Leistungsmonat" is not the KPI
 * card's "Fakturiert" — see `TREND_SERIES` for why the longer name is the honest one.
 * The subtitle repeats it on screen so a reader comparing the two figures is told,
 * rather than left to discover, that they answer different questions.
 */

const CARD = "flex h-full flex-col rounded-xl border border-border bg-card p-4 shadow-sm";

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-semibold text-card-foreground">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">{p.name}</span>
          {/* The contract value, not the charted Number. */}
          <span className="font-medium text-card-foreground">
            {fmtMoney(p.payload?.raw?.[p.dataKey])}
          </span>
        </div>
      ))}
    </div>
  );
}

function CardShell({ children }) {
  return (
    <section aria-labelledby="overview-trend-heading" className={CARD}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2
            id="overview-trend-heading"
            className="text-[13px] font-semibold text-card-foreground"
          >
            Umsatzentwicklung
          </h2>
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            Vertrags-Soll und periodenbezogene Fakturierung je Leistungsmonat
          </p>
        </div>
      </div>
      {children}
    </section>
  );
}

export default function OverviewTrendCard({
  revenueAnalytics,
  window: periodWindow,
  unsupported = false,
  loading = false,
  onRetry,
}) {
  const error = revenueAnalytics?.error;
  const rows = selectRevenueTrend(revenueAnalytics?.data, periodWindow);

  // "Gesamte Zeit" has no window, so the analytics call carries no period at all and
  // its trend describes whatever month the server resolved for itself. Charting that
  // under an all-time label would be a series nobody selected.
  if (unsupported) {
    return (
      <CardShell>
        <p
          data-testid="overview-trend-unsupported"
          className="mt-4 text-[12px] text-muted-foreground"
        >
          Der Umsatzverlauf folgt dem ausgewählten Zeitraum. Bitte wähle einen
          Zeitraum.
        </p>
      </CardShell>
    );
  }

  if (loading) {
    return (
      <CardShell>
        <div className="mt-3 space-y-2" aria-busy="true">
          <Skeleton className="h-[196px] w-full" />
          <Skeleton className="h-4 w-40" />
        </div>
      </CardShell>
    );
  }

  if (error) {
    return (
      <CardShell>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <p role="alert" className="text-[12px] text-destructive">
            Umsatzverlauf konnte nicht geladen werden.
          </p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            >
              Erneut laden
            </button>
          ) : null}
        </div>
      </CardShell>
    );
  }

  if (rows.length === 0) {
    return (
      <CardShell>
        <p className="mt-4 text-[12px] text-muted-foreground">
          Für diesen Zeitraum liegen keine Verlaufsdaten vor.
        </p>
      </CardShell>
    );
  }

  const selected = rows.find((r) => r.isSelected);

  return (
    <CardShell>
      {/* A legend written as text rather than recharts' own: the same markup then
          carries the colour swatch AND the name, so the series stay distinguishable
          when the hues are not. */}
      <ul
        data-testid="overview-trend-legend"
        className="mt-2 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-[11px]"
      >
        {TREND_SERIES.map((s) => (
          <li key={s.key} className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-2 w-4 shrink-0 rounded-sm"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-muted-foreground">{s.name}</span>
          </li>
        ))}
      </ul>

      <div className="mt-2 h-[200px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 10, bottom: 0, left: -14 }}>
            <CartesianGrid
              stroke="currentColor"
              className="text-border"
              strokeDasharray="3 4"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: "currentColor" }}
              className="text-muted-foreground"
            />
            <YAxis
              tickFormatter={compactAxisTick}
              tickLine={false}
              axisLine={false}
              width={52}
              tick={{ fontSize: 11, fill: "currentColor" }}
              className="text-muted-foreground"
            />
            <Tooltip content={<TrendTooltip />} cursor={{ strokeDasharray: "3 3" }} />
            {/* `connectNulls` stays false: a month the contract has no figure for is
                a gap, and joining across it would draw a line through months nobody
                measured. */}
            {TREND_SERIES.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                strokeDasharray={s.dash}
                dot={{ r: 2.5 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
            {/* The selected month, marked rather than merely last. Nothing is
                refetched for it — the point is already in the series. */}
            {selected && selected.expected !== null ? (
              <ReferenceDot
                x={selected.label}
                y={selected.expected}
                r={5}
                fill={TREND_SERIES[0].color}
                stroke="currentColor"
                className="text-card"
                strokeWidth={2}
                isFront
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* recharts tooltips are pointer-only, so the same figures exist as text. This
          is also the only place the numbers appear at all in a zero-width container. */}
      <table data-testid="overview-trend-table" className="sr-only">
        <caption>
          Erwarteter Umsatz und periodenbezogen fakturierter Umsatz je Monat
        </caption>
        <thead>
          <tr>
            <th scope="col">Monat</th>
            {TREND_SERIES.map((s) => (
              <th key={s.key} scope="col">
                {s.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{row.label}</th>
              {TREND_SERIES.map((s) => (
                <td key={s.key}>{fmtMoney(row.raw[s.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </CardShell>
  );
}
